/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import { Button, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { SpinnerIcon } from '@coursera/cds-icons';
import { API_IN_PROGRESS } from '@coursera/coursera-ui/lib/constants/sharedConstants';
import type { EnterpriseProperties } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import ErrorMessage from 'bundles/coursera-ui/components/extended/ErrorMessage';
import type { ApiState as ApiStatus } from 'bundles/coursera-ui/constants/apiNotificationConstants';
import { EnrollmentChoiceModalBody } from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModal';
import EnrollmentChoiceModalProductDescription from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalProductDescription';
import AutoenrolledCourseList from 'bundles/enterprise-legacy-learner-home/components/modals/AutoenrolledCourseList';
import getErrorMap from 'bundles/enterprise-legacy-learner-home/constants/ProgramJoinModalErrors';
import type { ProductCardCourseFragmentFragment as Course } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductCoursesQuery';
import type { NaptimeError } from 'bundles/naptimejs';
import { IdTypes } from 'bundles/product-features';
import { ProductFeaturesQuery } from 'bundles/program-home/productFeatureFlags';
import type { ProgramMembershipType } from 'bundles/program-home/types/ProgramMembershipAdapter';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

function getDefaultErrorMsg() {
  return _t('Error joining the program, please try again later');
}

type PropsFromCaller = {
  programId: string;
  programSlug: string;
  programName: string;
  programMembership?: ProgramMembershipType;
  thirdPartyOrganizationId: string;
  thirdPartyOrganizationSlug: string;
  orgName: string;
  onJoinProgram: () => void;
  apiStatus: ApiStatus;
  error?: NaptimeError | null;
  shouldUseJoinTrackingVerbiage?: boolean;
  isGwGProgram?: boolean;
  programRecommendationsAvailableCoursesCopy: boolean;
  autoEnrolledCoursesWithData?: Course[];
  userId: number;
  isAutoEnrolling: boolean;
};

type PropsFromRouter = {
  router: InjectedRouter;
};

export type PropsForModal = PropsFromCaller & PropsFromRouter;
const programJoinModalTrackingV3Name = 'program_join_modal';

type PropsForAction = {
  label: string;
  hasNavFromEOCModal: boolean;
  isAllowlisted: boolean;
  eventingEnterpriseProps?: { enterprise: EnterpriseProperties };
} & Pick<
  PropsForModal,
  'programId' | 'programName' | 'onJoinProgram' | 'apiStatus' | 'error' | 'thirdPartyOrganizationId'
>;

const styles = {
  container: (theme: Theme) => ({
    margin: 'var(--cds-spacing-400) var(--cds-spacing-600) 0 var(--cds-spacing-600)',
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-200)',
    },
  }),
  joinText: (theme: Theme) => ({
    marginTop: 'var(--cds-spacing-300)',
    [breakpoints.down('xs')]: {
      marginTop: 'var(--cds-spacing-200)',
    },
  }),
  autoenrollmentText: (theme: Theme) => ({
    marginTop: 'var(--cds-spacing-200)',
    [breakpoints.down('xs')]: {
      marginTop: 'var(--cds-spacing-100)',
    },
    borderBottom: `1px solid var(--cds-color-grey-50)`,
    paddingBottom: 'var(--cds-spacing-200)',
  }),
  trackingText: (theme: Theme) => ({
    whiteSpace: 'pre-wrap' as const,
    marginTop: 'var(--cds-spacing-200)',
    '&&': {
      fontWeight: 'bold' as const,
    },
  }),
};

const ProgramJoinModalAction = ({
  label,
  programId,
  programName,
  onJoinProgram,
  apiStatus,
  error,
  hasNavFromEOCModal,
  isAllowlisted,
  eventingEnterpriseProps,
}: PropsForAction) => {
  let trackingName: string;
  const track = useTracker();
  if (hasNavFromEOCModal && isAllowlisted) {
    trackingName = 'accept_allowlist_nav_from_eoc_modal';
  } else if (hasNavFromEOCModal) {
    trackingName = 'accept_invitation_nav_from_eoc_modal';
  } else {
    trackingName = 'accept_invitation';
  }
  const buttonTrackingV3Name = hasNavFromEOCModal ? 'join_program_and_enroll_in_course' : 'join_program';

  const programJoinButtonRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: { name: buttonTrackingV3Name },
    modal: { name: programJoinModalTrackingV3Name },
    ...eventingEnterpriseProps,
  });

  const onClick = () => {
    onJoinProgram();
    track('click_button', {
      button: { name: buttonTrackingV3Name },
      modal: { name: programJoinModalTrackingV3Name },
      ...eventingEnterpriseProps,
    });
  };

  return (
    <React.Fragment>
      <TrackedButton
        _refAltAsTrueRef={programJoinButtonRef}
        {...Button.defaultProps}
        trackingName={trackingName}
        trackingData={{ programId, programName }}
        data-e2e="ProgramJoinModal-join-button"
        disabled={apiStatus === API_IN_PROGRESS}
        onClick={onClick}
      >
        {label}
      </TrackedButton>
      {error && (
        <ErrorMessage
          style={{ marginTop: 15 }}
          error={error}
          defaultErrorMsg={
            (error.responseJSON && getErrorMap(programId)[error.responseJSON.errorCode]) || getDefaultErrorMsg()
          }
        />
      )}
    </React.Fragment>
  );
};

const ProgramJoinModal = ({
  programId,
  programSlug,
  programName,
  programMembership,
  orgName,
  onJoinProgram,
  apiStatus,
  error,
  thirdPartyOrganizationId,
  thirdPartyOrganizationSlug,
  router,
  isGwGProgram,
  programRecommendationsAvailableCoursesCopy,
  autoEnrolledCoursesWithData,
  userId,
  isAutoEnrolling,
  shouldUseJoinTrackingVerbiage = false,
}: PropsForModal) => {
  const theme = useTheme();

  const isReturning = programMembership?.isSoftDeletedMember;
  const hasNavFromEOCModal = Boolean(router.location.query.eoc);
  const eventingEnterpriseProps = {
    enterprise: { organizationSlug: thirdPartyOrganizationSlug, programSlug },
  };

  const modalRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_modal', {
    modal: { name: programJoinModalTrackingV3Name },
    ...eventingEnterpriseProps,
  });

  // Remove '%Fclips' check once Clips Redesign is generally available to all users. It's only necessary to make the redirect work for local development
  const isFromClipsRedirect =
    Boolean(router.location.query.redirectPath) &&
    (router.location.query.redirectPath.startsWith('/videos') ||
      router.location.query.redirectPath.startsWith('/clips'));

  const contentMap = {
    default: {
      title: _t('Join the learning program'),
      description: _t('A learning program is a repository of content curated by your organization.'),
      // eslint-disable-next-line no-nested-ternary
      buttonLabel: hasNavFromEOCModal
        ? _t('Join program and enroll')
        : isFromClipsRedirect
          ? _t('Join now and watch')
          : _t('Join program'),
    },
    isReturning: {
      title: _t('Join the learning program'),
      description: _t(
        'We’re happy to see you back! You were previously un-enrolled from your courses by your learning team but can re-enroll anytime. Your course progress has been saved so you will pick up right where you left off. Happy learning!'
      ),
      // eslint-disable-next-line no-nested-ternary
      buttonLabel: hasNavFromEOCModal
        ? _t('Return to program and enroll')
        : isFromClipsRedirect
          ? _t('Join now and watch')
          : _t('Return to program'),
    },
    isAutoEnrolling: {
      title: _t('Let’s get you to your destination'),
      description: _t('Join this program and get access to its content.'),
      buttonLabel: _t('Join program'),
    },
    isProgramRecommending: {
      title: _t('Join the learning program'),
      description: _t(
        'A learning program is a repository of content and featured courses to help you achieve your learning goals.'
      ),
      // eslint-disable-next-line no-nested-ternary
      buttonLabel: hasNavFromEOCModal
        ? _t('Join program and enroll')
        : isFromClipsRedirect
          ? _t('Join now and watch')
          : _t('Join program'),
    },
  };

  let buttonLabel: string;
  let description: string;
  let title: string;

  if (isAutoEnrolling) {
    title = contentMap.isAutoEnrolling.title;
    description = contentMap.isAutoEnrolling.description;
    buttonLabel = contentMap.isAutoEnrolling.buttonLabel;
  } else if (isReturning) {
    title = contentMap.isReturning.title;
    description = contentMap.isReturning.description;
    buttonLabel = contentMap.isReturning.buttonLabel;
  } else if (programRecommendationsAvailableCoursesCopy) {
    title = contentMap.isProgramRecommending.title;
    description = contentMap.isProgramRecommending.description;
    buttonLabel = contentMap.isProgramRecommending.buttonLabel;
  } else {
    title = contentMap.default.title;
    description = contentMap.default.description;
    buttonLabel = contentMap.default.buttonLabel;
  }

  return (
    <div className="ProgramJoinModal" data-e2e="ProgramJoinModal" ref={modalRef}>
      <EnrollmentChoiceModalBody
        headerTitle={title}
        button={
          <ProgramJoinModalAction
            programId={programId}
            programName={programName}
            onJoinProgram={onJoinProgram}
            apiStatus={apiStatus}
            error={error}
            label={buttonLabel}
            eventingEnterpriseProps={eventingEnterpriseProps}
            thirdPartyOrganizationId={thirdPartyOrganizationId}
            hasNavFromEOCModal={hasNavFromEOCModal}
            isAllowlisted={!!programMembership?.isAllowlisted}
          />
        }
      >
        {!isAutoEnrolling && <EnrollmentChoiceModalProductDescription />}
        <div css={styles.container(theme)}>
          {!isAutoEnrolling && (
            <Typography variant="h2" component="h3">
              {_t('You are invited to join the #{programName}', { programName })}
            </Typography>
          )}
          <Typography2 component="p" className="join-verbiage" css={styles.joinText(theme)}>
            {description}
          </Typography2>
          {autoEnrolledCoursesWithData && Boolean(autoEnrolledCoursesWithData?.length) && (
            <>
              <Typography2 component="p" css={styles.autoenrollmentText(theme)}>
                {_t(
                  'You will be automatically enrolled in the following courses by your organization. Find them in My Courses.'
                )}
              </Typography2>
              <AutoenrolledCourseList
                programId={programId}
                autoEnrolledCoursesWithData={autoEnrolledCoursesWithData}
                userId={userId}
              />
            </>
          )}
          {shouldUseJoinTrackingVerbiage && (
            <Typography2 component="p" className="tracking-verbiage" css={styles.trackingText(theme)}>
              <ProductFeaturesQuery idType={IdTypes.Program} id={programId}>
                {({ loading, features }) => {
                  if (loading) {
                    return <SpinnerIcon aria-busy aria-label={_t('Loading...')} />;
                  }

                  const customTrackingMessage = features.get('enterprise', 'programJoinTrackingCustomMessage');
                  if (customTrackingMessage) {
                    return customTrackingMessage;
                  }

                  if (isGwGProgram) {
                    return _t(
                      'By joining, you agree to #{orgName} tracking your grades and other activity in its learning program. Additionally, the content provider, Google, and support providers such as Global Grid for Learning (GG4L) may have access to your personal and learning data to provide supplementary services.',
                      { orgName }
                    );
                  }

                  return _t(
                    'By joining, you agree to #{orgName} tracking your grades and other activity in its learning program.',
                    { orgName }
                  );
                }}
              </ProductFeaturesQuery>
            </Typography2>
          )}
        </div>
      </EnrollmentChoiceModalBody>
    </div>
  );
};

export default ProgramJoinModal;
